import React from 'react';

const sizeMap = {
  sm: { width: 80, stroke: 8, label: 'text-lg', caption: 'text-[10px]' },
  md: { width: 120, stroke: 10, label: 'text-2xl', caption: 'text-xs' },
  lg: { width: 180, stroke: 12, label: 'text-4xl', caption: 'text-sm' },
};

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

function getRiskMeta(score) {
  if (score <= 30) return { label: 'Low', color: '#22c55e' };
  if (score <= 60) return { label: 'Medium', color: '#facc15' };
  if (score <= 80) return { label: 'High', color: '#fb923c' };
  return { label: 'Critical', color: '#f43f5e' };
}

export default function RiskGauge({ score = 0, size = 'md' }) {
  const clampedScore = Math.max(0, Math.min(100, Number(score) || 0));
  const config = sizeMap[size] || sizeMap.md;
  const width = config.width;
  const height = width * 0.62;
  const centerX = width / 2;
  const centerY = width / 2;
  const radius = width / 2 - config.stroke;
  const fullArcPath = describeArc(centerX, centerY, radius, 180, 360);
  const progressAngle = 180 + (clampedScore / 100) * 180;
  const progressPath = describeArc(centerX, centerY, radius, 180, progressAngle);
  const { label, color } = getRiskMeta(clampedScore);

  return (
    <div className="relative flex flex-col items-center" style={{ width }}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        <defs>
          <linearGradient id={`risk-gauge-gradient-${width}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="45%" stopColor="#facc15" />
            <stop offset="75%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#f43f5e" />
          </linearGradient>
        </defs>
        <path
          d={fullArcPath}
          fill="none"
          stroke="#1e293b"
          strokeWidth={config.stroke}
          strokeLinecap="round"
        />
        <path
          d={progressPath}
          fill="none"
          stroke={color}
          strokeWidth={config.stroke}
          strokeLinecap="round"
        />
        <path
          d={fullArcPath}
          fill="none"
          stroke={`url(#risk-gauge-gradient-${width})`}
          strokeWidth={Math.max(2, config.stroke / 3)}
          strokeLinecap="round"
          opacity="0.3"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pt-6">
        <span className={`${config.label} font-bold leading-none text-white`}>{Math.round(clampedScore)}</span>
        <span className={`${config.caption} mt-1 font-medium uppercase tracking-[0.18em] text-slate-400`}>
          {label}
        </span>
      </div>
    </div>
  );
}
